import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import { productGroups } from '../../data/products';
import SEO from '../../components/SEO';
import { useLanguage } from '../../i18n/LanguageContext';

const Products = () => {
  const { t, pick, path } = useLanguage();
  const visibleProductGroups = productGroups.filter(
    (group) => group.catalogVisible !== false && group.models.some((model) => model.catalogVisible !== false)
  );

  const pageStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #2d2d2d 100%)',
    paddingTop: '4rem'
  };

  const containerStyle = {
    maxWidth: '72rem',
    margin: '0 auto',
    padding: '2rem 1.5rem'
  };

  const headerStyle = {
    textAlign: 'center' as const,
    marginBottom: '3rem'
  };

  const titleStyle = {
    fontSize: '3rem',
    fontWeight: 'bold',
    color: 'white',
    marginBottom: '1rem'
  };

  const highlightStyle = {
    color: '#dfa926'
  };

  const descriptionStyle = {
    fontSize: '1.25rem',
    color: '#d1d5db',
    maxWidth: '48rem',
    margin: '0 auto'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(350px, 100%), 1fr))',
    gap: '2rem',
    marginBottom: '3rem'
  };

  const cardStyle = {
    background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.5) 0%, rgba(17, 17, 17, 0.5) 100%)',
    borderRadius: '0.75rem',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(58, 58, 58, 0.5)',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    cursor: 'pointer'
  };

  const imageStyle = {
    width: '100%',
    height: '600px',
    objectFit: 'cover' as const,
    background: 'linear-gradient(45deg, #3a3a3a, #1a1a1a)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#9ca3af',
    fontSize: '0.875rem'
  };

  const cardContentStyle = {
    padding: '1.5rem'
  };

  const cardTitleStyle = {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: 'white',
    marginBottom: '0.5rem'
  };

  const cardDescStyle = {
    color: '#9ca3af',
    marginBottom: '1rem',
    lineHeight: '1.5'
  };

  const badgeStyle = {
    display: 'inline-block',
    backgroundColor: 'rgba(223, 169, 38, 0.2)',
    color: '#dfa926',
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '500',
    marginRight: '0.5rem',
    marginBottom: '0.5rem'
  };

  const modelCardStyle = {
    background: 'rgba(58, 58, 58, 0.3)',
    borderRadius: '0.5rem',
    padding: '1rem',
    border: '1px solid rgba(75, 85, 99, 0.5)'
  };

  const featureListStyle = {
    listStyle: 'none',
    padding: 0,
    margin: 0
  };

  const featureItemStyle = {
    color: '#d1d5db',
    fontSize: '0.875rem',
    marginBottom: '0.25rem',
    display: 'flex',
    alignItems: 'flex-start'
  };

  const bulletStyle = {
    width: '0.25rem',
    height: '0.25rem',
    backgroundColor: '#dfa926',
    borderRadius: '50%',
    marginTop: '0.5rem',
    marginRight: '0.5rem',
    flexShrink: 0
  };

  return (
    <div style={pageStyle}>
      <SEO
        title={t('seo.products.title')}
        description={t('seo.products.description')}
        canonicalPath="/products"
        keywords={t('seo.products.keywords')}
      />
      <Navigation />
      
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h1 style={titleStyle}>
            {t('products.titlePre')} <span style={highlightStyle}>{t('products.titleHl')}</span>
          </h1>
          <p style={descriptionStyle}>
            {t('products.desc')}
          </p>
        </div>

        {/* Category landing links */}
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' as const, marginBottom: '2.5rem' }}>
          <Link to={path('/exterior-doors')} style={{ color: '#dfa926', border: '1px solid rgba(223, 169, 38, 0.4)', borderRadius: '9999px', padding: '0.5rem 1.25rem', textDecoration: 'none', fontSize: '0.9rem' }}>
            {t('categories.exteriorDoors.h1')}
          </Link>
          <Link to={path('/storage-doors')} style={{ color: '#dfa926', border: '1px solid rgba(223, 169, 38, 0.4)', borderRadius: '9999px', padding: '0.5rem 1.25rem', textDecoration: 'none', fontSize: '0.9rem' }}>
            {t('categories.storageDoors.h1')}
          </Link>
          <Link to={path('/configurator')} style={{ color: 'black', backgroundColor: '#dfa926', borderRadius: '9999px', padding: '0.5rem 1.25rem', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 600 }}>
            {t('configurator.title')}
          </Link>
        </div>

        <div style={gridStyle}>
          {visibleProductGroups.map((group) => (
            <div
              key={group.id}
              style={cardStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(223, 169, 38, 0.5)';
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(58, 58, 58, 0.5)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <Link to={path(`/products/${group.models[0]?.slug}`)} style={{ textDecoration: 'none' }}>
                <div style={imageStyle}>
                  {group.models[0]?.image ? (
                    <img
                      src={group.models[0].image}
                      alt={pick(group, 'name')}
                      loading="lazy"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        if (target.parentNode) {
                          (target.parentNode as HTMLElement).innerHTML = `<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #9ca3af; font-size: 0.875rem;">${pick(group, 'name')}</div>`;
                        }
                      }}
                    />
                  ) : (
                    <div>{pick(group, 'name')}</div>
                  )}
                </div>
              </Link>

              <div style={cardContentStyle}>
                <h3 style={cardTitleStyle}>
                  <Link to={path(`/products/${group.models[0]?.slug}`)} style={{ color: 'inherit', textDecoration: 'none' }}>
                    {pick(group.models[0], 'name')}
                  </Link>
                </h3>
                <p style={cardDescStyle}>{pick(group, 'description')}</p>

                <div>
                  <span style={badgeStyle}>CE-marked</span>
                  {(group.models[0]?.configurations || []).map((configuration) => (
                    <span key={configuration.id} style={badgeStyle}>{pick(configuration, 'name')}</span>
                  ))}
                </div>

                <ul style={{ ...featureListStyle, marginTop: '1rem' }}>
                  {pick(group.models[0], 'keyFeatures').slice(0, 3).map((feature: string, index: number) => (
                    <li key={index} style={featureItemStyle}>
                      <span style={bulletStyle}></span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={path(`/products/${group.models[0]?.slug}`)}
                  style={{ ...modelCardStyle, color: '#dfa926', cursor: 'pointer', textDecoration: 'none', display: 'block', marginTop: '1rem', fontWeight: '500' }}
                >
                  {t('products.viewDetails')} →
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: '3rem' }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(223, 169, 38, 0.1) 0%, rgba(223, 169, 38, 0.05) 100%)',
            padding: '2rem',
            borderRadius: '0.75rem',
            border: '1px solid rgba(223, 169, 38, 0.2)'
          }}>
            <h3 style={{ color: 'white', fontSize: '1.5rem', marginBottom: '1rem' }}>
              {t('products.customTitle')}
            </h3>
            <p style={{ color: '#d1d5db', marginBottom: '1.5rem' }}>
              {t('products.customDesc')}
            </p>
            <Link
              to={path('/contact')}
              style={{
                backgroundColor: '#dfa926',
                color: 'black',
                padding: '0.75rem 2rem',
                borderRadius: '0.5rem',
                textDecoration: 'none',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                display: 'inline-block'
              }}
              onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#c8941f'}
              onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = '#dfa926'}
            >
              {t('products.quote')}
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Products;
